import {useTranslation} from "react-i18next";
import {ColumnDef, SortingState, Updater} from "@tanstack/react-table";
import {Info, Pencil, Plus, Trash2} from "lucide-react";
import {OrderTime} from "@/models/order-time";
import {DataTable, sortableColumnHeader} from "@/components/data-table";
import {Text} from "@/components/tailwind/text";
import {Button} from "@/components/tailwind/button";
import {formatDateString, formatDurationToHHMM} from "@/lib/utils";
import {useState} from "react";
import {useUserSettingsStore} from "@/stores/user-settings";
import {useBillingUnitLabel} from "@/hooks/use-billing-unit-label";

interface OrderTimesTableProps {
    orderTimes: OrderTime[];
    onAdd: () => void;
    onEdit: (orderTime: OrderTime) => void;
    onDelete: (orderTime: OrderTime) => void;
    isOrderCancelled?: boolean;
    isOrderCompleted?: boolean;
    hasHelper: boolean;
    onSortingChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
}

export function OrderTimesTable({
    orderTimes,
    onAdd,
    onEdit,
    onDelete,
    isOrderCancelled = false,
    isOrderCompleted = false,
    hasHelper,
    onSortingChange
}: OrderTimesTableProps) {
    const {t} = useTranslation("orders");
    const {unitLabel} = useBillingUnitLabel();
    const { tableSettings, setOrderTimesSorting } = useUserSettingsStore();
    const [sorting, setSorting] = useState<SortingState>(() => {
        // Initialize from stored settings
        if (tableSettings.orderTimesSortBy) {
            return [{
                id: tableSettings.orderTimesSortBy,
                desc: tableSettings.orderTimesSortOrder === 'desc'
            }];
        }
        return [];
    });
    
    // Handle sorting changes
    const handleSortingChange = (updaterOrValue: Updater<SortingState>) => {
        const newSorting = typeof updaterOrValue === 'function' 
            ? updaterOrValue(sorting)
            : updaterOrValue;
            
        setSorting(newSorting);
        
        if (newSorting.length > 0) {
            const sortBy = newSorting[0].id;
            const sortOrder = newSorting[0].desc ? 'desc' : 'asc';
            setOrderTimesSorting(sortBy, sortOrder);
            if (onSortingChange) {
                onSortingChange(sortBy, sortOrder);
            }
        }
    };

    const columns: ColumnDef<OrderTime>[] = [
        {
            accessorKey: "date",
            header: ({column}) => sortableColumnHeader(column, "orderDetail.times.table.headers.date", "orders"),
            cell: ({row}) => <Text>{formatDateString(row.original.date)}</Text>
        },
        {
            accessorKey: "startTime", 
            header: ({column}) => sortableColumnHeader(column, "orderDetail.times.table.headers.startTime", "orders"),
            cell: ({row}) => <Text>{row.original.startTime}</Text>
        },
        {
            accessorKey: "endTime", 
            header: t("orderDetail.times.table.headers.endTime"),
            cell: ({row}) => <Text>{row.original.endTime}</Text>
        },
        {
            accessorKey: "hours",
            header: t("orderDetail.times.table.headers.hours"),
            cell: ({row}) => (
                <Text className="tabular-nums">
                    {formatDurationToHHMM(row.original.startTime, row.original.endTime)}
                </Text>
            )
        },
        {
            accessorKey: "servicePoints",
            header: t("orderDetail.times.table.headers.serviceHours", {unit: unitLabel}),
            cell: ({row}) => <Text>{row.original.servicePoints}</Text>
        },
        {
            accessorKey: "totalPoints",
            header: t("orderDetail.times.table.headers.totalPoints"),
            cell: ({row}) => <Text>{row.original.totalPoints}</Text>
        },
        {
            accessorKey: "totalFee",
            header: t("orderDetail.times.table.headers.totalFee"),
            cell: ({row}) => <Text>{row.original.totalFee.toFixed(2)} €</Text>
        },
        {
            id: "actions",
            header: t("orderDetail.times.table.headers.actions"),
            cell: ({row}) => {
                const isDisabled = isOrderCancelled || isOrderCompleted || !hasHelper;
                return (
                    <div className="flex gap-2">
                        <Button
                            onClick={() => onEdit(row.original)}
                            plain
                            aria-label={t("orderDetail.times.table.actions.edit")}
                            data-testid={`edit-order-time-${row.original.id}`}
                            disabled={isDisabled}
                        >
                            <Pencil className={`h-4 w-4 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`} />
                        </Button>
                        <Button
                            onClick={() => onDelete(row.original)}
                            plain
                            aria-label={t("orderDetail.times.table.actions.delete")}
                            data-testid={`delete-order-time-${row.original.id}`}
                            disabled={isDisabled}
                        >
                            <Trash2 className={`h-4 w-4 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:text-red-500 dark:hover:text-red-400'}`} />
                        </Button>
                    </div>
                );
            }
        }
    ];

    const EmptyState = () => {
        const isDisabled = isOrderCancelled || isOrderCompleted || !hasHelper;
        return (
            <div className="text-center py-8">
                <Text className="text-gray-500 mb-4">
                    {t("orderDetail.times.table.empty.description")}
                </Text>
                <Button
                    onClick={onAdd}
                    className="inline-flex items-center gap-2"
                    data-testid="add-first-order-time"
                    disabled={isDisabled}
                >
                    <Plus className={`h-4 w-4 ${isDisabled ? 'opacity-50' : ''}`} />
                    {t("orderDetail.times.table.empty.action")}
                </Button>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {!hasHelper && (
                <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 flex items-start gap-3">
                    <Info 
                        className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" 
                        aria-label={t("orderDetail.times.noHelperInfo")}
                    />
                    <Text className="text-blue-700 dark:text-blue-300">
                        {t("orderDetail.times.noHelperMessage")}
                    </Text>
                </div>
            )}

            {orderTimes.length > 0 && (
                <div className="flex justify-end">
                    <Button
                        onClick={onAdd}
                        outline
                        data-testid="add-order-time"
                        disabled={isOrderCancelled || isOrderCompleted || !hasHelper}
                    >
                        <Plus data-slot="icon" className={isOrderCancelled || isOrderCompleted || !hasHelper ? 'opacity-50' : ''} />
                        {t("orderDetail.times.table.actions.add")}
                    </Button>
                </div>
            )}

            {orderTimes.length === 0 ? (
                <EmptyState />
            ) : (
                <DataTable
                    columns={columns}
                    data={orderTimes}
                    sorting={sorting}
                    onSortingChange={handleSortingChange}
                    data-testid="order-times-table"
                />
            )}
        </div>
    );
}
